import React, { useEffect, useState, useCallback, useRef } from "react";
import ConsortMark from "./ConsortMark";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Flame, Heart, MessageCircle, Sparkles, User, Shield, Crown, LogOut, Gauge, ShieldCheck, X, Gift } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const NAV = [
  { to: "/discovery", label: "Discover", Icon: Flame },
  { to: "/likes", label: "Likes", Icon: Heart },
  { to: "/matches", label: "Matches", Icon: MessageCircle },
  { to: "/subscription", label: "Membership", Icon: Sparkles },
];

function CountBadge({ n, className = "" }) {
  if (!n) return null;
  return (
    <span data-testid="nav-badge" className={`min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-black text-[10px] font-semibold inline-flex items-center justify-center ${className}`}>
      {n > 99 ? "99+" : n}
    </span>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const active = (to) => loc.pathname.startsWith(to);
  const [counts, setCounts] = useState({ unread_messages: 0, new_likes: 0, reactions: 0 });
  const timer = useRef(null);
  const prevReactions = useRef(null);

  const fetchCounts = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/counts");
      setCounts(data);
      if (prevReactions.current !== null && (data.reactions || 0) > prevReactions.current) {
        toast("Someone reacted to your message", { icon: "❤️" });
      }
      prevReactions.current = data.reactions || 0;
    } catch { /* not signed in / transient */ }
  }, []);

  useEffect(() => {
    fetchCounts();
    timer.current = setInterval(fetchCounts, 20000);
    return () => clearInterval(timer.current);
  }, [fetchCounts]);

  // refresh counts when navigating (e.g. after reading a chat or acting on likes)
  useEffect(() => { fetchCounts(); }, [loc.pathname, fetchCounts]);

  const countFor = (to) => (to === "/matches" ? counts.unread_messages : to === "/likes" ? counts.new_likes : 0);

  // Grandfather prompt: encourage members who haven't verified their phone (dismissible per-account).
  const bannerKey = user ? `hide_verify_banner_${user.user_id}` : null;
  const [showVerifyBanner, setShowVerifyBanner] = useState(false);
  useEffect(() => {
    if (!user || user.role === "admin") { setShowVerifyBanner(false); return; }
    const needs = !user.verification?.phone;
    const dismissed = bannerKey ? localStorage.getItem(bannerKey) === "1" : false;
    setShowVerifyBanner(needs && !dismissed);
  }, [user, bannerKey]);
  const dismissBanner = () => { if (bannerKey) localStorage.setItem(bannerKey, "1"); setShowVerifyBanner(false); };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <header className="sticky top-0 z-40 glass">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-5 h-16">
          <Link to="/discovery" className="flex items-center gap-2" data-testid="brand-logo">
            <ConsortMark className="text-primary" size={22} />
            <span className="font-display text-xl tracking-tight"><span className="gold-text">Consort</span></span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map(({ to, label, Icon }) => (
              <Link
                key={to}
                to={to}
                data-testid={`nav-${label.toLowerCase()}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${active(to) ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground"}`}
              >
                <Icon size={17} strokeWidth={1.5} /> {label}
                <CountBadge n={countFor(to)} />
              </Link>
            ))}
          </nav>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu" className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center border border-white/10">
                <User size={18} strokeWidth={1.5} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="card-surface w-56">
              <div className="px-3 py-2">
                <p className="text-sm font-medium">{user?.profile?.first_name || "You"}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.effective_tier} member</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => nav("/profile")} data-testid="menu-profile"><User size={16} className="mr-2" strokeWidth={1.5}/>My Profile</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/highlights")} data-testid="menu-highlights"><Sparkles size={16} className="mr-2" strokeWidth={1.5}/>My Highlights</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/verification")} data-testid="menu-verification"><Shield size={16} className="mr-2" strokeWidth={1.5}/>Verification</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/billing")} data-testid="menu-billing"><Sparkles size={16} className="mr-2" strokeWidth={1.5}/>Billing</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/gift")} data-testid="menu-gift"><Gift size={16} className="mr-2" strokeWidth={1.5}/>Gift a membership</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/redeem")} data-testid="menu-redeem"><Gift size={16} className="mr-2" strokeWidth={1.5}/>Redeem a gift</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/settings")} data-testid="menu-settings">Settings & Privacy</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/safety")} data-testid="menu-safety"><Shield size={16} className="mr-2" strokeWidth={1.5}/>Safety Center</DropdownMenuItem>
              {user?.role === "admin" && (
                <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin"><Gauge size={16} className="mr-2" strokeWidth={1.5}/>Admin Dashboard</DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="menu-logout" className="text-destructive-foreground">
                <LogOut size={16} className="mr-2" strokeWidth={1.5}/>Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {showVerifyBanner && (
        <div data-testid="verify-phone-banner" className="border-b border-primary/20 bg-primary/10">
          <div className="max-w-6xl mx-auto flex items-center gap-3 px-5 py-2.5">
            <ShieldCheck size={17} className="text-primary shrink-0" strokeWidth={1.75} />
            <p className="text-sm text-foreground/90 flex-1 min-w-0">Verify your phone to earn a <span className="text-primary font-medium">Verified</span> badge and boost trust with matches.</p>
            <button onClick={() => nav("/verify-phone")} data-testid="verify-banner-cta" className="text-sm font-medium text-black bg-primary hover:bg-primary/90 rounded-full px-4 py-1.5 shrink-0">Verify now</button>
            <button onClick={dismissBanner} data-testid="verify-banner-dismiss" className="text-muted-foreground hover:text-foreground shrink-0" aria-label="Dismiss"><X size={16} strokeWidth={1.75} /></button>
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 md:px-5 py-6">
        <Outlet />
      </main>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 glass border-t border-white/10">
        <div className="flex items-center justify-around h-16">
          {NAV.map(({ to, label, Icon }) => (
            <Link key={to} to={to} data-testid={`bottomnav-${label.toLowerCase()}`}
              className={`relative flex flex-col items-center gap-0.5 text-[11px] ${active(to) ? "text-primary" : "text-muted-foreground"}`}>
              <div className="relative">
                <Icon size={20} strokeWidth={1.5} />
                <CountBadge n={countFor(to)} className="absolute -top-2 -right-3 border border-background" />
              </div>
              {label}
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
}
